import { forwardRef, useState, type InputHTMLAttributes, type CSSProperties, type ReactNode } from "react";
import { useEmbedTokens, useReducedMotion, motionSafe } from "../theme";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "style" | "type"> {
  label?: ReactNode;
  invalid?: boolean;
}

// Visually-hidden native input (preserves keyboard + SR) + styled box. Inline styles
// can't style :checked, so the box is driven from React state mirrored off the input.
export const EmbedCheckbox = forwardRef<HTMLInputElement, Props>(function EmbedCheckbox(
  { label, invalid = false, checked, defaultChecked = false, onChange, onFocus, onBlur, id, ...rest }: Props,
  ref,
) {
  const t = useEmbedTokens();
  const reduced = useReducedMotion();
  const [focused, setFocused] = useState(false);
  const [internal, setInternal] = useState<boolean>(Boolean(defaultChecked));
  const isControlled = checked !== undefined;
  const isChecked = isControlled ? Boolean(checked) : internal;

  const hiddenInput: CSSProperties = {
    position: "absolute", width: 1, height: 1, margin: -1, padding: 0, border: 0,
    overflow: "hidden", clip: "rect(0 0 0 0)", clipPath: "inset(50%)", whiteSpace: "nowrap",
  };
  const borderColor = invalid ? t.colors.error : isChecked ? t.colors.primary : t.colors.border;
  const box: CSSProperties = {
    width: 18, height: 18, flex: "none", borderRadius: t.borderRadiusSm,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    border: `1px solid ${borderColor}`,
    background: isChecked ? (invalid ? t.colors.error : t.colors.primary) : "transparent",
    boxShadow: focused ? `0 0 0 3px ${t.colors.primaryTint}` : "none",
    transition: motionSafe(reduced, "border-color .12s, background .12s, box-shadow .12s"),
  };
  // The glyph sits on the primary fill (or the error fill when invalid), so it
  // takes that fill's own "on" colour rather than a fixed white.
  const checkGlyph: CSSProperties = { color: invalid ? t.colors.onDanger : t.colors.onAction, fontSize: 12, lineHeight: 1, fontWeight: 700 };
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: t.spacing.sm, cursor: "pointer", minHeight: t.pointer === "fine" ? 24 : t.size.touchTarget }}>
      <input
        ref={ref} id={id} type="checkbox"
        checked={isChecked}
        onChange={(e) => { if (!isControlled) setInternal(e.target.checked); onChange?.(e); }}
        onFocus={(e) => { setFocused(true); onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); onBlur?.(e); }}
        aria-invalid={invalid || undefined}
        style={hiddenInput} {...rest} />
      <span aria-hidden="true" style={box}>{isChecked && <span style={checkGlyph}>✓</span>}</span>
      {label && <span style={{ fontSize: t.fontSize.sm, color: t.colors.text }}>{label}</span>}
    </label>
  );
});
